/**
 * NetobsTickBadgeContent (NO-2) — inner content of the TICK debug tool.
 *
 * Server-tick health snapshot:
 *   green  — mean < 50% budget
 *   yellow — mean 50-90% budget
 *   red    — mean 90-200% budget
 *   black  — mean >200% budget (catastrophic)
 *
 * Stale detection: if no `netobs:tick` arrives for >2s (4× emit cadence),
 * the badge dims to "—" and shows "stale". Catches the case where the
 * server is so slow it can't even broadcast.
 *
 * Visibility is no longer controlled inside this component — it always
 * renders when mounted. DebugHUDHost decides when to mount it (driven by
 * DebugToolsContext + DebugLauncher).
 */

import React, { useEffect, useState } from 'react';
import { NETOBS_COLORS, netobsHealthColor } from './colors';
import { useNetobsTick } from './useNetobsTick';
import { CopyButton } from '../debug/CopyButton';

const STALE_AFTER_MS = 2000;
const POLL_STALE_INTERVAL_MS = 500;

export const NetobsTickBadgeContent: React.FC = () => {
  const snap = useNetobsTick();
  const [now, setNow] = useState<number>(Date.now());

  // Lightweight 500ms tick to detect staleness (otherwise we'd never
  // re-render to flip green→stale once messages stop).
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), POLL_STALE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const stale = !snap || (now - snap.receivedAt) > STALE_AFTER_MS;
  const color = snap && !stale
    ? netobsHealthColor(snap.meanMs, snap.budgetMs)
    : '#555';

  // E1 breakdown — top-3 hottest subsystems by mean. Only shown when
  // the server is on a build that emits the `subsystems` field.
  const topSubsystems: Array<[string, number]> = (() => {
    if (!snap || stale || !snap.subsystems) return [];
    return Object.entries(snap.subsystems)
      .map(([name, s]) => [name, s.meanMs] as [string, number])
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
  })();

  return (
    <div
      data-testid="netobs-tick-badge"
      style={{
        color: '#ddd',
        fontFamily: 'monospace',
        fontSize: 10,
        lineHeight: 1.35,
        padding: '4px 6px',
        userSelect: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <span
          data-testid="netobs-tick-dot"
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: color,
            boxShadow: stale ? 'none' : `0 0 6px ${color}88`,
          }}
        />
        <span style={{ color: '#7af', letterSpacing: '0.04em' }}>TICK</span>
        <span style={{ flex: 1 }} />
        <CopyButton getText={() => {
          if (!snap || stale) return `SERVER TICK @ ${new Date().toISOString()}\nstatus: stale`;
          const lines = [
            `SERVER TICK @ ${new Date().toISOString()}`,
            `mean:    ${snap.meanMs.toFixed(2)} ms`,
            `max:     ${snap.maxMs.toFixed(2)} ms`,
            `budget:  ${snap.budgetMs.toFixed(2)} ms`,
            `samples: ${snap.sampleCount}`,
            `subsystems:`,
            ...topSubsystems.map(([name, ms]) => `  ${name}: ${ms.toFixed(2)} ms`),
          ];
          return lines.join('\n');
        }} />
        {snap && !stale ? (
          <>
            <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>
              {snap.meanMs.toFixed(1)}ms
            </span>
            <span style={{ color: '#666', fontSize: 9 }}>
              / {snap.budgetMs.toFixed(1)}
            </span>
            {snap.maxMs > snap.budgetMs && (
              <span
                style={{
                  color: NETOBS_COLORS.HEALTH_BAD,
                  fontSize: 9,
                  marginLeft: 2,
                }}
                title={`max: ${snap.maxMs.toFixed(1)}ms`}
              >
                !
              </span>
            )}
          </>
        ) : (
          <span style={{ color: '#666', fontStyle: 'italic' }}>stale</span>
        )}
      </div>
      {topSubsystems.length > 0 && (
        <div
          data-testid="netobs-tick-subsystems"
          style={{
            color: '#888',
            fontSize: 9,
            marginLeft: 16,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.25,
          }}
        >
          {topSubsystems.map(([name, ms], i) => (
            <span key={name}>
              {i > 0 && <span style={{ color: '#444', margin: '0 4px' }}>·</span>}
              <span style={{ color: '#9aa' }}>{name}</span>{' '}
              <span style={{ color: '#bbb' }}>{ms.toFixed(2)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
